import React, { useState } from 'react';
import { Users, Eye, EyeOff, ImagePlus, RefreshCw, Download, X, Trash2, Plus, Send, Loader2, Pencil, Check } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PortraitModal = ({ npc, onClose }) => (
  <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={onClose}>
    <div className="relative max-w-lg w-full" onClick={e => e.stopPropagation()}>
      <button onClick={onClose} className="absolute -top-3 -right-3 bg-slate-800 border border-white/10 rounded-full p-1.5 hover:bg-slate-700 transition-colors z-10" data-testid="portrait-modal-close">
        <X className="h-4 w-4 text-slate-300" />
      </button>
      <img src={npc.portrait_url} alt={npc.name} className="w-full rounded-lg shadow-2xl" />
      <div className="mt-3 flex items-center justify-between">
        <p className="text-slate-300 font-medium">{npc.name}</p>
        <a
          href={npc.portrait_url}
          download={`${npc.name.replace(/\s+/g, '_')}_portrait.png`}
          className="flex items-center gap-1.5 text-xs text-blue-400 hover:text-blue-300 transition-colors bg-slate-800/80 border border-blue-500/20 rounded-lg px-3 py-1.5"
          data-testid={`npc-download-${npc.npc_id}`}
        >
          <Download className="h-3.5 w-3.5" />
          Download
        </a>
      </div>
    </div>
  </div>
);

const EDITABLE_FIELDS = [
  { key: 'name', label: 'Name', type: 'text' },
  { key: 'role', label: 'Role', type: 'text' },
  { key: 'description', label: 'Appearance', type: 'textarea' },
  { key: 'personality', label: 'Personality', type: 'text' },
  { key: 'speaking_style', label: 'Voice', type: 'text' },
  { key: 'goals', label: 'Goals', type: 'text' },
  { key: 'relationship', label: 'Relationship', type: 'text' },
  { key: 'status', label: 'Status', type: 'text' },
  { key: 'secrets', label: 'Secrets (GM only)', type: 'textarea' },
];

const InlineEditForm = ({ npc, onSave, onCancel }) => {
  const [fields, setFields] = useState(() => {
    const initial = {};
    EDITABLE_FIELDS.forEach(f => { initial[f.key] = npc[f.key] || ''; });
    return initial;
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    // Only send fields that actually changed
    const changes = {};
    EDITABLE_FIELDS.forEach(f => {
      if (fields[f.key] !== (npc[f.key] || '')) {
        changes[f.key] = fields[f.key];
      }
    });
    if (Object.keys(changes).length === 0) {
      onCancel();
      return;
    }
    await onSave(npc.npc_id, changes);
    setSaving(false);
  };

  return (
    <div className="mt-2 space-y-1.5 border-t border-white/5 pt-2" data-testid={`npc-edit-form-${npc.npc_id}`}>
      {EDITABLE_FIELDS.map(f => (
        <div key={f.key}>
          <label className="text-[10px] font-mono text-slate-500 uppercase tracking-wider">{f.label}</label>
          {f.type === 'textarea' ? (
            <textarea
              value={fields[f.key]}
              onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
              className="w-full bg-slate-950/60 border border-white/10 text-slate-200 text-xs rounded px-2 py-1.5 resize-y min-h-[48px] focus:border-blue-400/50 focus:outline-none"
              rows={2}
              data-testid={`npc-edit-${f.key}-${npc.npc_id}`}
            />
          ) : (
            <input
              type="text"
              value={fields[f.key]}
              onChange={e => setFields(prev => ({ ...prev, [f.key]: e.target.value }))}
              className="w-full bg-slate-950/60 border border-white/10 text-slate-200 text-xs rounded px-2 py-1.5 focus:border-blue-400/50 focus:outline-none"
              data-testid={`npc-edit-${f.key}-${npc.npc_id}`}
            />
          )}
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 flex items-center justify-center gap-1 text-xs py-1.5 rounded bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 disabled:opacity-40 transition-all"
          data-testid={`npc-edit-save-${npc.npc_id}`}
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="flex-1 text-xs py-1.5 rounded bg-slate-800/50 border border-white/5 text-slate-400 hover:text-slate-300 hover:bg-slate-700/50 transition-all"
          data-testid={`npc-edit-cancel-${npc.npc_id}`}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export const NPCListTab = ({ npcs, onDelete, onSelectNpc, onToggleActive, onNpcUpdated, campaignId }) => {
  const [generatingId, setGeneratingId] = useState(null);
  const [viewingNpc, setViewingNpc] = useState(null);
  const [npcImageModel, setNpcImageModel] = useState('openai');
  const [npcPrompt, setNpcPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [editingNpcId, setEditingNpcId] = useState(null);

  const generatePortrait = async (e, npc) => {
    e.stopPropagation();
    setGeneratingId(npc.npc_id);
    try {
      await axios.post(
        `${API}/npcs/${npc.npc_id}/generate-portrait`,
        { image_model: npcImageModel },
        { withCredentials: true, timeout: 120000 }
      );
      if (onNpcUpdated) onNpcUpdated();
      toast.success(`Portrait generated for ${npc.name}!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate portrait');
    } finally {
      setGeneratingId(null);
    }
  };

  const handleDeleteNpc = async (e, npcId, npcName) => {
    e.stopPropagation();
    try {
      if (onDelete) await onDelete(npcId);
      setEditingNpcId(null);
      toast.success(`${npcName} removed`);
    } catch (err) {
      toast.error('Failed to delete NPC');
    }
  };

  const handleGenerateNpc = async () => {
    if (!npcPrompt.trim() || !campaignId) return;
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/campaigns/${campaignId}/generate-npc`,
        { prompt: npcPrompt.trim() },
        { withCredentials: true, timeout: 60000 }
      );
      setNpcPrompt('');
      if (onNpcUpdated) onNpcUpdated();
      const action = response.data.action || 'created';
      const name = response.data.name || 'NPC';
      if (action === 'updated') {
        toast.success(`${name} updated!`);
      } else if (action === 'dedup') {
        toast.success(response.data.message || 'Duplicates removed');
      } else {
        toast.success(`${name} created!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate NPC');
    } finally {
      setGenerating(false);
    }
  };

  const handleInlineSave = async (npcId, changes) => {
    try {
      await axios.patch(
        `${API}/npcs/${npcId}`,
        changes,
        { withCredentials: true }
      );
      setEditingNpcId(null);
      if (onNpcUpdated) onNpcUpdated();
      toast.success('NPC updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update NPC');
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-blue-400 font-mono" data-testid="npc-tab-title">Notable Characters</h3>
      
      {/* Manual NPC Card Generator */}
      <div className="bg-slate-900/60 border border-white/5 rounded-lg p-3 space-y-2" data-testid="npc-generator">
        <p className="text-xs text-slate-400 font-mono">Create or Edit NPC</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={npcPrompt}
            onChange={(e) => setNpcPrompt(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !generating && handleGenerateNpc()}
            placeholder="e.g. Create Anakin Skywalker / Update Rex's status / Remove duplicates"
            className="flex-1 bg-slate-950/50 border border-white/10 focus:border-blue-400/50 text-slate-200 rounded-md px-3 py-2 text-xs placeholder:text-slate-600 focus:outline-none"
            disabled={generating}
            data-testid="npc-prompt-input"
          />
          <button
            onClick={handleGenerateNpc}
            disabled={generating || !npcPrompt.trim()}
            className="px-3 py-2 rounded-md text-xs font-medium transition-all flex items-center gap-1.5 bg-blue-500/20 border border-blue-500/30 text-blue-300 hover:bg-blue-500/30 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="npc-generate-btn"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            {generating ? 'Working...' : 'Go'}
          </button>
        </div>
      </div>

      <div className="flex gap-1" data-testid="npc-portrait-model-selector">
        <button onClick={() => setNpcImageModel('openai')} className={`flex-1 text-[10px] py-1 rounded transition-all ${npcImageModel === 'openai' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' : 'text-slate-500 hover:text-slate-400 border border-transparent'}`}>GPT Image</button>
        <button onClick={() => setNpcImageModel('leonardo')} className={`flex-1 text-[10px] py-1 rounded transition-all ${npcImageModel === 'leonardo' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' : 'text-slate-500 hover:text-slate-400 border border-transparent'}`}>Leonardo AI</button>
      </div>
      {npcs.length > 0 ? (
        <div className="space-y-3">
          {npcs.map((npc) => (
            <div
              key={npc.npc_id}
              className={`rounded-lg transition-all border overflow-hidden ${npc.active !== false ? 'bg-slate-900/50 border-white/5 hover:border-blue-400/30' : 'bg-slate-950/30 border-white/3 opacity-50'}`}
              data-testid={`npc-${npc.npc_id}`}
            >
              {/* Portrait */}
              {npc.portrait_url && (
                <button
                  onClick={() => setViewingNpc(npc)}
                  className="w-full aspect-[3/2] overflow-hidden bg-slate-950/50 group cursor-pointer relative"
                  data-testid={`npc-portrait-${npc.npc_id}`}
                >
                  <img src={npc.portrait_url} alt={npc.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                    <span className="text-white/0 group-hover:text-white/80 text-xs font-medium transition-colors">Click to view</span>
                  </div>
                </button>
              )}

              <div className="p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 text-left min-w-0 overflow-hidden">
                    <p className="text-slate-200 font-medium truncate" title={npc.name}>{npc.name}</p>
                    <p className="text-blue-400 text-xs truncate">{npc.role}</p>
                    {editingNpcId !== npc.npc_id && (
                      <>
                        {npc.personality && <p className="text-slate-400 text-xs mt-1 italic line-clamp-1">{npc.personality}</p>}
                        {npc.speaking_style && <p className="text-purple-400/70 text-xs truncate">Voice: {npc.speaking_style}</p>}
                        {npc.goals && <p className="text-amber-400/70 text-xs truncate">Wants: {npc.goals}</p>}
                        {npc.relationship && <p className="text-emerald-400/70 text-xs truncate">{npc.relationship}</p>}
                        {npc.description && <p className="text-slate-500 text-xs mt-1 line-clamp-2">{npc.description}</p>}
                      </>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0 ml-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingNpcId(editingNpcId === npc.npc_id ? null : npc.npc_id); }}
                      className={`p-1.5 rounded transition-all ${editingNpcId === npc.npc_id ? 'text-blue-400 bg-blue-500/15' : 'text-slate-500 hover:text-blue-400 hover:bg-blue-500/10'}`}
                      title="Quick edit"
                      data-testid={`npc-edit-${npc.npc_id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); onToggleActive(npc.npc_id, npc.active !== false); }}
                      className={`p-1.5 rounded transition-all ${npc.active !== false ? 'text-emerald-400 hover:bg-emerald-500/15' : 'text-slate-600 hover:bg-slate-700/30'}`}
                      title={npc.active !== false ? 'Active — included in AI context' : 'Inactive — excluded from AI context'}
                      data-testid={`npc-toggle-${npc.npc_id}`}
                    >
                      {npc.active !== false ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={(e) => handleDeleteNpc(e, npc.npc_id, npc.name)}
                      className="p-1.5 rounded text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-all"
                      title="Delete NPC card"
                      data-testid={`npc-delete-${npc.npc_id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Inline Edit Form */}
                {editingNpcId === npc.npc_id && (
                  <InlineEditForm
                    npc={npc}
                    onSave={handleInlineSave}
                    onCancel={() => setEditingNpcId(null)}
                  />
                )}

                {/* Generate / Regenerate Portrait Button */}
                <button
                  onClick={(e) => generatePortrait(e, npc)}
                  disabled={generatingId === npc.npc_id}
                  className={`mt-2 w-full flex items-center justify-center gap-1.5 text-xs py-1.5 rounded-md transition-all border ${
                    generatingId === npc.npc_id
                      ? 'bg-purple-500/10 border-purple-500/20 text-purple-300 cursor-wait'
                      : 'bg-slate-800/50 border-white/5 text-slate-400 hover:text-purple-300 hover:border-purple-500/30 hover:bg-purple-500/10'
                  }`}
                  data-testid={`npc-gen-portrait-${npc.npc_id}`}
                >
                  {generatingId === npc.npc_id ? (
                    <>
                      <RefreshCw className="h-3 w-3 animate-spin" />
                      Generating...
                    </>
                  ) : npc.portrait_url ? (
                    <>
                      <RefreshCw className="h-3 w-3" />
                      Regenerate Portrait
                    </>
                  ) : (
                    <>
                      <ImagePlus className="h-3 w-3" />
                      Generate Portrait
                    </>
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500 text-sm text-center py-8">No NPC cards yet. Use the input above to create one!</p>
      )}

      {viewingNpc && <PortraitModal npc={viewingNpc} onClose={() => setViewingNpc(null)} />}
    </div>
  );
};
